import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { toast } from "sonner";
import { Sparkles, X, Check, ExternalLink } from "lucide-react";

const BUSINESSES = ["PYN CAPITAL", "DOA / PRO PARTNER", "BIOVAULT", "PERSONAL"];
const KINDS = ["OWED_TO_ME", "INVOICE", "DEAL_CLOSING"];

const CONFIDENCE_STYLE = {
  high:   { bg: "bg-[rgba(34,197,94,0.06)]",  text: "text-[#22c55e]",  label: "HIGH CONFIDENCE" },
  medium: { bg: "bg-[rgba(245,166,35,0.05)]", text: "text-[#F5A623]",  label: "MEDIUM CONFIDENCE" },
  low:    { bg: "bg-[rgba(239,68,68,0.05)]",  text: "text-[#ef4444]",  label: "LOW CONFIDENCE · REVIEW" },
};

const MoneyCandidates = ({ onConfirmed }) => {
  const [items, setItems] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});

  const load = () => api.moneyCandidates().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.scanMoneyCandidates();
      toast("Todoist scanned", {
        description: `${res.new_or_updated} new · ${res.pending} pending confirmation`,
      });
      load();
    } catch (e) {
      toast("Scan failed", { description: e?.response?.data?.detail || "Connect Todoist in Settings first." });
    } finally {
      setScanning(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEdit({
      kind: c.suggested_kind,
      label: c.raw_content.slice(0, 120),
      party: c.suggested_party || "",
      business: "PYN CAPITAL",
      amount: c.suggested_amount,
      due_date: "",
    });
  };

  const confirm = async (c) => {
    const payload = editingId === c.id ? edit : {
      kind: c.suggested_kind,
      label: c.raw_content.slice(0, 120),
      party: c.suggested_party || "Unknown",
      business: "PYN CAPITAL",
      amount: c.suggested_amount,
    };
    if (!payload.party) {
      toast("Party required", { description: "Type who owes / who's paying." });
      return;
    }
    try {
      await api.confirmMoneyCandidate(c.id, payload);
      toast("Confirmed → Money");
      setEditingId(null);
      load();
      onConfirmed?.();
    } catch (e) {
      toast("Confirm failed", { description: e?.response?.data?.detail || "" });
    }
  };

  const dismiss = async (c) => {
    await api.dismissMoneyCandidate(c.id);
    toast("Dismissed");
    load();
  };

  if (items.length === 0) {
    return (
      <section data-testid="money-candidates">
        <SectionHeader
          code="?"
          label="TODOIST · MONEY CANDIDATES"
          caption="Scan Todoist for money-related tasks — flagged for confirmation, never auto-created."
        />
        <div className="op-panel p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="text-[#A0A5B5] text-sm">
            No candidates flagged. Click scan to check your Todoist for $ patterns.
          </div>
          <button
            onClick={scan}
            disabled={scanning}
            data-testid="money-scan"
            className="op-btn inline-flex items-center gap-2"
          >
            <Sparkles size={12} className={scanning ? "animate-pulse" : ""} />
            {scanning ? "SCANNING…" : "SCAN TODOIST"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="money-candidates">
      <SectionHeader
        code="?"
        label="TODOIST · MONEY CANDIDATES"
        caption="Flagged for confirmation. Nothing enters the ledger without you saying so."
      />
      <div className="op-panel">
        <div className="flex items-center justify-between px-6 py-3 border-b border-white/[0.05]">
          <span className="op-tag">{items.length} PENDING · REVIEW BELOW</span>
          <button
            onClick={scan}
            disabled={scanning}
            data-testid="money-scan"
            className="op-tag hover:text-[#00E5FF] inline-flex items-center gap-1.5"
          >
            <Sparkles size={10} className={scanning ? "animate-pulse" : ""} />
            {scanning ? "SCANNING…" : "RESCAN"}
          </button>
        </div>
        <div className="divide-y divide-white/[0.04]">
          {items.map((c) => {
            const style = CONFIDENCE_STYLE[c.confidence] || CONFIDENCE_STYLE.low;
            const isEditing = editingId === c.id;
            return (
              <div
                key={c.id}
                data-testid={`candidate-${c.id}`}
                className={`px-6 py-4 ${style.bg}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className={`op-tag mb-2 ${style.text}`}>{style.label}</div>
                    <div className="text-white text-sm mb-2 leading-snug">{c.raw_content}</div>
                    {c.raw_url && (
                      <a
                        href={c.raw_url}
                        target="_blank"
                        rel="noreferrer"
                        className="op-tag hover:text-[#00E5FF] inline-flex items-center gap-1.5"
                      >
                        SOURCE TODOIST <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => startEdit(c)}
                          className="op-btn text-[9px] px-3 py-1.5"
                          data-testid={`candidate-edit-${c.id}`}
                        >
                          EDIT
                        </button>
                        <button
                          onClick={() => confirm(c)}
                          className="op-btn op-btn--primary text-[9px] px-3 py-1.5 inline-flex items-center gap-1"
                          data-testid={`candidate-confirm-${c.id}`}
                        >
                          <Check size={10} /> CONFIRM
                        </button>
                        <button
                          onClick={() => dismiss(c)}
                          className="op-btn text-[9px] px-3 py-1.5 inline-flex items-center gap-1"
                          data-testid={`candidate-dismiss-${c.id}`}
                        >
                          <X size={10} /> DISMISS
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4">
                    <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })} className="op-input op-mono text-xs !py-2 col-span-2 md:col-span-1">
                      {KINDS.map((k) => <option key={k}>{k}</option>)}
                    </select>
                    <input placeholder="Label" value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="op-input !py-2 text-xs col-span-2" />
                    <input placeholder="Party" value={edit.party} onChange={(e) => setEdit({ ...edit, party: e.target.value })} className="op-input !py-2 text-xs" />
                    <input type="number" step="0.01" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} className="op-input op-mono !py-2 text-xs" />
                    <input type="date" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className="op-input op-mono !py-2 text-xs" />
                    <select value={edit.business} onChange={(e) => setEdit({ ...edit, business: e.target.value })} className="op-input op-mono text-xs !py-2 col-span-2 md:col-span-3">
                      {BUSINESSES.map((b) => <option key={b}>{b}</option>)}
                    </select>
                    <div className="col-span-2 md:col-span-3 flex justify-end gap-2">
                      <button onClick={() => setEditingId(null)} className="op-btn text-[9px] px-3 py-1.5">CANCEL</button>
                      <button onClick={() => confirm(c)} className="op-btn op-btn--primary text-[9px] px-3 py-1.5" data-testid={`candidate-save-${c.id}`}>
                        SAVE + CONFIRM
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="border border-white/[0.06] p-2">
                      <div className="op-tag mb-0.5">SUGGESTED KIND</div>
                      <div className="op-mono text-xs text-white">{c.suggested_kind}</div>
                    </div>
                    <div className="border border-white/[0.06] p-2">
                      <div className="op-tag mb-0.5">AMOUNT</div>
                      <div className="op-mono text-xs text-white">${Number(c.suggested_amount).toLocaleString()}</div>
                    </div>
                    <div className="border border-white/[0.06] p-2">
                      <div className="op-tag mb-0.5">PARTY</div>
                      <div className="op-mono text-xs text-white">{c.suggested_party || "—"}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MoneyCandidates;
